import { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fmtPrice } from '../utils/format';

const RANGES = ['7D', '14D', '30D', '90D'];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="tt-date">{label}</div>
      <div className="tt-price">{fmtPrice(payload[0].value)}</div>
    </div>
  );
}

export default function PriceChart({ historyData, range, onRangeChange, isDark }) {
  const chartData = useMemo(() => {
    if (!historyData) return [];
    return historyData.prices.map(([ts, price]) => ({
      date: new Date(ts).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }),
      price,
    }));
  }, [historyData]);

  const prices = chartData.map(d => d.price);
  const isUp = prices.length > 1 && prices[prices.length - 1] >= prices[0];
  const color = isUp ? '#00c853' : '#ff4d4d';
  const gridColor = isDark ? '#1e1e1e' : '#e4e6eb';
  const tickColor = isDark ? '#555' : '#999';

  const min = prices.length ? Math.min(...prices) : 0;
  const max = prices.length ? Math.max(...prices) : 0;

  const tickCount = chartData.length;
  const step = Math.max(1, Math.floor(tickCount / 6));
  const ticks = chartData.filter((_, i) => i % step === 0 || i === tickCount - 1).map(d => d.date);

  return (
    <div className="price-chart">
      <div className="chart-controls">
        {RANGES.map(r => (
          <button
            key={r}
            className={`range-btn ${range === r ? 'active' : ''}`}
            onClick={() => onRangeChange(r)}
          >
            {r}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
          <defs>
            <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.25} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis dataKey="date" ticks={ticks} tick={{ fill: tickColor, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis
            domain={[min * 0.995, max * 1.005]}
            tickFormatter={v => fmtPrice(v)}
            tick={{ fill: tickColor, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={80}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey="price" stroke={color} strokeWidth={2} fill="url(#priceGrad)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
